import type { MapDocument, TilesetRef } from "../document/types.ts";
import { hashWords } from "../random.ts";
import { createPaintStack, type GroundRule, type PaintStack, insideZone, paintAllows, paintAt, type ZoneTest } from "./ground-at.ts";
import { dabTarget, latticePointsInCircle } from "./scatter-density.ts";
import type { ScatterKit, ScatterShape } from "./scatter-entries.ts";
import { baseAt, clearCollected, coverAt, coverOccupancy, coverUnder, gapClear, membersInCircle, occupy, type ScatterOccupancy } from "./scatter-occupancy.ts";
import type { PropRef } from "./templates.ts";

/**
 * The prop brush's dab (ADR-123 decisions 2.1 to 2.5): seeded dart throwing on the authoring cells inside a circle.
 * Candidate `i` of a dab is drawn from `h = hash(seed, i)` alone, so a test can name the candidate that placed any
 * prop: its cell offset in the square of side `2R + 1` cells from `hash(h, 1)` and `hash(h, 2)` (rejected outside
 * the circle), its entry from `hash(h, 3)` over the weights, its flips from the bits of `hash(h, 4)`. Its lead foot
 * point is set on the drawn cell corner and its laying edge snapped to the nearest cell edge (`cellOffset`'s
 * rounding, ADR-040), and it is placed when, at that final position, it keeps every rule of decision 2.3 as the
 * amendment of 2026-10-13 reads it: its base never on the base of another prop unless the set allows it, its gap kept
 * among the set's own props. A set that clears ground cover removes, with each placement that is not itself cover, the
 * known cover of the document whose foot cell is one of the placement's base cells (A3); the dab only says which, and
 * the stroke writes the removals as one command.
 */

/**
 * Candidates a dab may try for each prop it asks for (Bridson's figure for Poisson-disc sampling).
 */
export const ATTEMPTS_PER_PROP = 30;

/**
 * One placement: the entry's index among the usable entries, its anchor in tiles on the pixel lattice (the prop's
 * position for a frame entry, the template's anchor for a template entry), and its random flips.
 */
export interface ScatterPlacement {
  readonly entry: number;
  readonly x: number;
  readonly y: number;
  readonly flipX: boolean;
  readonly flipY: boolean;
}

/**
 * Why a dab stopped: it placed its target, spent its attempts, or ran out of room in the stroke or the layer.
 */
export type DabStop = "target" | "attempts" | "cap";

/**
 * A dab's inputs: the prepared set, the map, the world's tilesets (for the ground) and its id (for zone kinds), the
 * centre as a cell corner, the radius in tiles, the density from 1 to 100, the dab's seed, the props the stroke may
 * still place, and the cover it may still clear (unbounded when left out).
 */
export interface DabInput {
  readonly kit: ScatterKit;
  readonly map: MapDocument;
  readonly tilesets: readonly TilesetRef[];
  readonly world: string;
  readonly centreX: number;
  readonly centreY: number;
  readonly radius: number;
  readonly density: number;
  readonly seed: number;
  readonly room: number;
  readonly clearRoom?: number;
}

/**
 * How many tries each rule refused in a dab (ADR-123's amendment of 2026-10-13, A4.1): a try is a candidate inside the
 * circle at its final position, counted once under the first rule it fails, in the order the dab tests them.
 */
export interface Refusals {
  readonly edge: number;
  readonly props: number;
  readonly gap: number;
  readonly ground: number;
}

/**
 * What a dab did: its placements in the order made, the cover they cleared in the order cleared, why it stopped, the
 * count it asked for, how many candidates it drew, how many of them were tries and the tries each rule refused.
 */
export interface DabResult {
  readonly placements: ScatterPlacement[];
  readonly cleared: PropRef[];
  readonly stop: DabStop;
  readonly target: number;
  readonly candidates: number;
  readonly tried: number;
  readonly refused: Refusals;
}

/**
 * What `fits` answers: 0 for a candidate that keeps every rule it tests, else the code of the first it fails; the
 * ground, read after `fits`, is the fourth. A code less one is the slot of the occupancy's counters.
 */
const FITS = 0;
const REFUSED_EDGE = 1;
const REFUSED_PROPS = 2;
const REFUSED_GAP = 3;
const REFUSED_GROUND = 4;

/**
 * The first rule a candidate, laid at its final position, fails among those a placement is entered by: every piece's art on
 * the map; unless the set allows placing on top, none of its base cells (every piece's footprint cells or art strip
 * and foot cell) in the base of another prop, placed earlier or by hand, whatever set it is of, and, for a shape that
 * is not itself known cover, none in the foot cell of cover the stroke has placed (cover from the document paints no
 * base); and every piece's foot
 * point its pair gap from the foot points of the set's props, and of the others' when the set keeps its gap from
 * them. The rules are tried in that order and the first to fail is the answer.
 *
 * @param occupancy The occupancy.
 * @param kit The prepared set.
 * @param shape The entry's shape.
 * @param kx The lead's laying corner column, in cells.
 * @param ky The lead's laying corner row, in cells.
 * @param left The anchor's left edge in pixels.
 * @param top The anchor's top edge in pixels.
 * @param mapWidth The map's width in pixels.
 * @param mapHeight The map's height in pixels.
 * @returns `FITS` when it may be placed, before its ground is read, else `REFUSED_EDGE`, `REFUSED_PROPS` or `REFUSED_GAP`.
 */
function fits(occupancy: ScatterOccupancy, kit: ScatterKit, shape: ScatterShape, kx: number, ky: number, left: number, top: number, mapWidth: number, mapHeight: number): number {
  const pieces = shape.pieces;
  for (let i = 0; i < pieces.length; i += 6) {
    if (left + (pieces[i] as number) < 0 || top + (pieces[i + 1] as number) < 0 || left + (pieces[i + 2] as number) > mapWidth || top + (pieces[i + 3] as number) > mapHeight) {
      return REFUSED_EDGE;
    }
  }
  if (!kit.allowsPlacingOnTop) {
    const base = shape.base;
    for (let i = 0; i < base.length; i += 2) {
      const column = kx + (base[i] as number);
      const row = ky + (base[i + 1] as number);
      if (baseAt(occupancy, column, row) || (!shape.cover && coverAt(occupancy, column, row))) {
        return REFUSED_PROPS;
      }
    }
  }
  for (let i = 0; i < pieces.length; i += 6) {
    if (!gapClear(occupancy, left + (pieces[i + 4] as number), top + (pieces[i + 5] as number), shape.gaps[i / 6] as number, kit.keepsGapFromOthers)) {
      return REFUSED_GAP;
    }
  }
  return FITS;
}

/**
 * A dab's result, the counters copied out of the occupancy's array: the one small object a dab makes.
 *
 * @param refusals The occupancy's counters, edge, props, gap and ground.
 * @param placements The placements.
 * @param cleared The cover cleared.
 * @param stop Why the dab stopped.
 * @param target The count it asked for.
 * @param candidates How many candidates it drew.
 * @returns The result; its tries are the refused ones and the placed.
 */
function result(refusals: Uint32Array, placements: ScatterPlacement[], cleared: PropRef[], stop: DabStop, target: number, candidates: number): DabResult {
  const edge = refusals[0] as number;
  const props = refusals[1] as number;
  const gap = refusals[2] as number;
  const ground = refusals[3] as number;
  return { placements, cleared, stop, target, candidates, tried: edge + props + gap + ground + placements.length, refused: { edge, props, gap, ground } };
}

/**
 * Whether the set's rule and then the entry's pass at the foot of every piece of a candidate laid at its final
 * position: each piece is a prop, so each is read where it stands (ADR-123's note of 2026-10-13, P1.6-T250). The
 * paint is read into the dab's one reused stack and the zone test reads the piece's foot through `probe`, so no
 * candidate allocates.
 *
 * @param kit The prepared set, for the set's rule.
 * @param rule The entry's rule, if any.
 * @param shape The entry's shape.
 * @param left The anchor's left edge in pixels.
 * @param top The anchor's top edge in pixels.
 * @param map The map.
 * @param tilesets The world's tilesets.
 * @param stack The reused paint stack.
 * @param inZone The zone test, reading `probe`.
 * @param probe The foot point the zone test reads, set here.
 * @returns True when every piece's foot passes both rules.
 */
function groundPasses(kit: ScatterKit, rule: GroundRule | undefined, shape: ScatterShape, left: number, top: number, map: MapDocument, tilesets: readonly TilesetRef[], stack: PaintStack, inZone: ZoneTest, probe: { x: number; y: number }): boolean {
  const pieces = shape.pieces;
  for (let i = 0; i < pieces.length; i += 6) {
    probe.x = left + (pieces[i + 4] as number);
    probe.y = top + (pieces[i + 5] as number);
    paintAt(map, tilesets, kit.tileSize, probe.x, probe.y, stack);
    if (!paintAllows(kit.ground, stack, inZone) || !paintAllows(rule, stack, inZone)) {
      return false;
    }
  }
  return true;
}

/**
 * Places one dab of the brush. The occupancy is grown over the dab's reach and every placement is entered in it as
 * it is made, so the dab's later candidates, and later dabs on the same occupancy, keep clear of it; a preview takes
 * a mark first and restores it after (`markOccupancy`). The target is `dabTarget` over the lattice points of the
 * circle on the map, less the set's props already there; the dab draws at most `ATTEMPTS_PER_PROP` candidates for
 * each prop it may place, the target or the room left if less, so a dab is bounded by the stroke's cap however
 * large its circle, and stops at the target, at the end of its attempts, or when the next placement's pieces would not
 * fit the room left. A candidate's ground is read once at its foot point, and it lands only where the set's own rule
 * and then its entry's both pass, a zone kind read at the same point in the open world. A placement that would clear
 * more cover than the stroke may still clear is not made and the dab stops with `cap`, as it does when the next
 * placement's pieces would not fit the room left.
 *
 * @param input The dab's inputs.
 * @param occupancy The stroke's occupancy, changed in place.
 * @returns What the dab placed, why it stopped and what its tries were refused for.
 */
export function scatterDab(input: DabInput, occupancy: ScatterOccupancy): DabResult {
  const { kit, map } = input;
  const refusals = occupancy.refusals;
  refusals.fill(0);
  const tileSize = kit.tileSize;
  const cell = kit.cellPx;
  const radius = input.radius * kit.cellsPerTile;
  const reach = Math.ceil(kit.reach / cell) + 1;
  coverOccupancy(occupancy, input.centreX - radius - reach, input.centreY - radius - reach, input.centreX + radius + reach, input.centreY + radius + reach);
  const centreX = input.centreX * cell;
  const centreY = input.centreY * cell;
  const radiusPx = input.radius * tileSize;
  const limit = radiusPx * radiusPx;
  const area = latticePointsInCircle(input.centreX, input.centreY, radius, map.width * kit.cellsPerTile, map.height * kit.cellsPerTile) * cell * cell;
  const target = dabTarget(input.density, area, kit.meanGapSq, membersInCircle(occupancy, centreX, centreY, radiusPx));
  const placements: ScatterPlacement[] = [];
  const cleared: PropRef[] = [];
  if (target === 0) {
    return result(refusals, placements, cleared, "target", target, 0);
  }
  if (input.room <= 0) {
    return result(refusals, placements, cleared, "cap", target, 0);
  }
  let room = input.room;
  let clearRoom = input.clearRoom ?? Number.MAX_SAFE_INTEGER;
  const budget = Math.min(target, room) * ATTEMPTS_PER_PROP;
  const side = 2 * radius + 1;
  const word = new Uint32Array(1);
  const mapWidth = map.width * tileSize;
  const mapHeight = map.height * tileSize;
  const probe = { x: 0, y: 0 };
  const stack = createPaintStack();
  const inZone: ZoneTest = (zone) => insideZone(map, input.world, zone, tileSize, probe.x, probe.y);
  let i = 0;
  while (i < budget) {
    word[0] = i;
    i += 1;
    const h = hashWords(input.seed, word, 1);
    word[0] = 1;
    const ox = (hashWords(h, word, 1) % side) - radius;
    word[0] = 2;
    const oy = (hashWords(h, word, 1) % side) - radius;
    if (ox * ox + oy * oy > radius * radius) {
      continue;
    }
    word[0] = 3;
    const pick = hashWords(h, word, 1) % kit.totalWeight;
    let entry = 0;
    while ((kit.cumulative[entry] as number) <= pick) {
      entry += 1;
    }
    word[0] = 4;
    const bits = hashWords(h, word, 1);
    const shapes = kit.shapes[entry] as readonly (ScatterShape | undefined)[];
    const variant = (shapes[1] !== undefined && (bits & 1) !== 0 ? 1 : 0) | (shapes[2] !== undefined && (bits & 2) !== 0 ? 2 : 0);
    const shape = shapes[variant] as ScatterShape;
    const footX = (input.centreX + ox) * cell;
    const footY = (input.centreY + oy) * cell;
    const kx = Math.round((footX - shape.footX + shape.leadX) / cell);
    const ky = input.centreY + oy;
    const left = kx * cell - shape.leadX;
    const top = footY - shape.footY;
    const finalX = left + shape.footX;
    if ((finalX - centreX) * (finalX - centreX) + (footY - centreY) * (footY - centreY) > limit) {
      continue;
    }
    const refused = fits(occupancy, kit, shape, kx, ky, left, top, mapWidth, mapHeight);
    if (refused !== FITS) {
      refusals[refused - 1] = (refusals[refused - 1] as number) + 1;
      continue;
    }
    const rule = kit.entries[entry]?.ground;
    if ((kit.ground !== undefined || rule !== undefined) && !groundPasses(kit, rule, shape, left, top, map, input.tilesets, stack, inZone, probe)) {
      refusals[REFUSED_GROUND - 1] = (refusals[REFUSED_GROUND - 1] as number) + 1;
      continue;
    }
    const pieces = shape.gaps.length;
    if (pieces > room) {
      return result(refusals, placements, cleared, "cap", target, i);
    }
    const under = kit.clearsGroundCover && !shape.cover ? coverUnder(occupancy, shape, kx, ky) : 0;
    if (under > clearRoom) {
      return result(refusals, placements, cleared, "cap", target, i);
    }
    occupy(occupancy, shape, kx, ky, left, top);
    if (under > 0) {
      clearCollected(occupancy, cleared);
      clearRoom -= under;
    }
    placements.push({ entry, x: left / tileSize, y: top / tileSize, flipX: shape.flipX, flipY: shape.flipY });
    room -= pieces;
    if (placements.length === target) {
      return result(refusals, placements, cleared, "target", target, i);
    }
    if (room === 0 || (under > 0 && clearRoom === 0)) {
      return result(refusals, placements, cleared, "cap", target, i);
    }
  }
  return result(refusals, placements, cleared, "attempts", target, i);
}
